// NOTE: `DEFAULT_BO_URL` import was removed per
// `docs/requirements/fable-stories-no-user-facing-backend-url-v1.md` §3.1.
// `getBoUrl()` now returns `''` by default — same-origin via Nginx proxy in
// production, vite dev proxy in development. The Backend URL is no longer a
// user-configurable field.

const KEYS = {
  nickname: 'fable_nickname',
  boUrl: 'fable_bo_url',
  boSecret: 'fable_bo_secret',
  history: 'fable_history',
  scrollPositions: 'fable_scroll_positions',
  columnCount: 'fable_column_count',
  yikeKeyId: 'fable_yike_key_id',
  yikeKeySecret: 'fable_yike_key_secret',
  yikeEndpoint: 'fable_yike_endpoint',
  paidStories: 'fable_paid_stories',
  /** @deprecated Legacy one-time token from /users/claim. Kept for backward compat. */
  userToken: 'fable_user_token',
  cachedStars: 'fable_cached_stars',
  /** JWT access token issued by POST /v1/fable/auth/login or /register */
  accessToken: 'fable_access_token',
  /** JWT refresh token for re-issuing access tokens without re-login */
  refreshToken: 'fable_refresh_token',
  /** UTC ms when the access token expires (for proactive refresh) */
  accessTokenExpiresAt: 'fable_access_token_expires_at',
} as const;

// ── Masonry column count (size adjuster) ──────────────────────────────────────
export const MIN_COLUMNS = 2;
export const MAX_COLUMNS = 6;
export const DEFAULT_COLUMNS = 5;

export function getColumnCount(): number {
  const raw = parseInt(localStorage.getItem(KEYS.columnCount) || '', 10);
  if (Number.isNaN(raw)) return DEFAULT_COLUMNS;
  return Math.max(MIN_COLUMNS, Math.min(MAX_COLUMNS, raw));
}
export function setColumnCount(n: number): void {
  const clamped = Math.max(MIN_COLUMNS, Math.min(MAX_COLUMNS, n));
  localStorage.setItem(KEYS.columnCount, String(clamped));
}

export interface HistoryEntry {
  captureId: string;
  spaceId: string;
  title: string;
  spaceName: string;
  readAt: number;
}

// ── Nickname ──────────────────────────────────────────────────────────────────
export function getNickname(): string | null {
  return localStorage.getItem(KEYS.nickname);
}
export function setNickname(name: string): void {
  localStorage.setItem(KEYS.nickname, name.trim());
}

// ── BO Connection (same-origin by default) ────────────────────────────────────
//
// Per `docs/requirements/fable-stories-no-user-facing-backend-url-v1.md`:
//   • Default = `''` → `api.buildUrl()` resolves against `window.location.href`
//     → in production Nginx proxies `^/(v1|auth)/` to the BO on the same server.
//   • Stale legacy values (`http://127.0.0.1:*`, `http://localhost:*`,
//     `http://0.0.0.0:*`, `http://[::1]:*`) are auto-purged on every read so a
//     v1 deploy self-heals every existing browser without manual localStorage
//     cleanup. The purge is idempotent — after the first read the key is gone.
//
// `setBoUrl()` is kept (used by `applyBackendOverrideFromQueryString` for the
// operator `?backend=URL` escape hatch). It is NOT wired to any user-facing
// input in v1.

// Loopback: 127.x.x.x / localhost / 0.0.0.0 / ::1
// Private LAN: 10.x.x.x / 172.16-31.x.x / 192.168.x.x
// Any of these mean the stored URL points at someone's dev machine, not the
// deployed server. Purge them so the SPA always falls back to same-origin.
const HOST_LOCAL_URL_RE = /^https?:\/\/(?:127(?:\.\d+){3}|localhost|0\.0\.0\.0|\[::1\]|10(?:\.\d+){3}|172\.(?:1[6-9]|2\d|3[01])(?:\.\d+){2}|192\.168(?:\.\d+){2})(?::\d+)?(?:\/.*)?$/i;

export function getBoUrl(): string {
  const raw = localStorage.getItem(KEYS.boUrl);
  if (!raw) return '';
  if (HOST_LOCAL_URL_RE.test(raw.trim())) {
    // Self-heal stale browser localStorage pointing at a dev-machine BO
    // (loopback or private LAN). Under same-origin default this would
    // silently break the SPA on the public deploy. Purge is idempotent.
    try { localStorage.removeItem(KEYS.boUrl); } catch { /* private mode */ }
    return '';
  }
  return raw;
}

export function setBoUrl(url: string): void {
  const cleaned = url.replace(/\/$/, '').trim();
  if (cleaned) localStorage.setItem(KEYS.boUrl, cleaned);
  else localStorage.removeItem(KEYS.boUrl);
}

/**
 * Operator-only diagnostic escape hatch. Reads `?backend=URL` once on boot,
 * persists into localStorage, then strips the param from the URL bar so a
 * refresh does not re-apply it. The legacy `?secret=...` param is honoured
 * only as a wipe — when present (any value) the stored secret is cleared,
 * never set, because the local-profile secret is now injected server-side
 * (`docs/requirements/fable-stories-no-user-facing-backend-url-v1.md` §6.2).
 *
 * Use cases:
 *   - Point a single browser at a non-default BO:
 *       `https://j31.pub/fable/?backend=https://staging.example.com`
 *   - Reset to same-origin:  `https://j31.pub/fable/?backend=`
 *
 * Idempotent: silently no-ops when neither param is present, and writes/strips
 * exactly once per boot. Invalid URLs are warned about (console) and ignored
 * without overwriting the stored value.
 */
export function applyBackendOverrideFromQueryString(loc: Location): void {
  const params = new URLSearchParams(loc.search);
  const hasBackend = params.has('backend');
  const hasSecret = params.has('secret');
  if (!hasBackend && !hasSecret) return;

  if (hasBackend) {
    const raw = (params.get('backend') ?? '').trim();
    if (raw === '') {
      try { localStorage.removeItem(KEYS.boUrl); } catch { /* private mode */ }
    } else {
      try {
        const u = new URL(raw);
        setBoUrl(u.toString().replace(/\/$/, ''));
      } catch {
        // Leave the existing value alone on invalid input.
        // eslint-disable-next-line no-console
        console.warn('[fable-stories] ?backend= override is not a valid URL — ignored:', raw);
      }
    }
  }

  if (hasSecret) {
    // v2: any `?secret=` param value is treated as a wipe instruction so a
    // shared link cannot re-arm a stale secret. The server-side Nginx layer
    // is the only authoritative source of `X-CaptureX-Local-Profile-Secret`.
    setBoSecret('');
  }

  // Strip both params so a refresh does not re-apply them.
  try {
    params.delete('backend');
    params.delete('secret');
    const clean = `${loc.pathname}${params.toString() ? `?${params.toString()}` : ''}${loc.hash}`;
    window.history.replaceState(null, '', clean);
  } catch { /* history API unavailable in some embeds */ }
}

/**
 * @deprecated v2 — server-side Nginx injects `X-CaptureX-Local-Profile-Secret`
 *             per `docs/requirements/fable-stories-no-user-facing-backend-url-v1.md`
 *             §6.2. Always returns `''` so any legacy caller short-circuits and
 *             stops sending a stale browser-stored value.
 */
export function getBoSecret(): string {
  return '';
}

/**
 * v2 — kept only as a defensive wipe (`setBoSecret('')`). Non-empty values are
 * accepted but logged as a deprecation warning so operators notice old code
 * paths. Spec: `docs/requirements/fable-stories-no-user-facing-backend-url-v1.md` §6.2.
 */
export function setBoSecret(secret: string): void {
  if (secret.trim()) {
    // eslint-disable-next-line no-console
    console.warn('[fable-stories] setBoSecret() ignored non-empty value — '
      + 'secret is now injected server-side; see '
      + 'docs/requirements/fable-stories-no-user-facing-backend-url-v1.md §6.2.');
  }
  try { localStorage.removeItem(KEYS.boSecret); } catch { /* private mode */ }
}

// ── Read History ──────────────────────────────────────────────────────────────
const MAX_HISTORY = 50;

export function getHistory(): HistoryEntry[] {
  try {
    return JSON.parse(localStorage.getItem(KEYS.history) || '[]');
  } catch {
    return [];
  }
}

export function addToHistory(entry: Omit<HistoryEntry, 'readAt'>): void {
  const history = getHistory().filter((h) => h.captureId !== entry.captureId);
  history.unshift({ ...entry, readAt: Date.now() });
  if (history.length > MAX_HISTORY) history.splice(MAX_HISTORY);
  localStorage.setItem(KEYS.history, JSON.stringify(history));
}

export function clearHistory(): void {
  localStorage.removeItem(KEYS.history);
}

// ── Yike (万镜一刻) credentials ───────────────────────────────────────────────
export function getYikeKeyId(): string { return localStorage.getItem(KEYS.yikeKeyId) || ''; }
export function setYikeKeyId(v: string): void {
  if (v.trim()) localStorage.setItem(KEYS.yikeKeyId, v.trim());
  else localStorage.removeItem(KEYS.yikeKeyId);
}
export function getYikeKeySecret(): string { return localStorage.getItem(KEYS.yikeKeySecret) || ''; }
export function setYikeKeySecret(v: string): void {
  if (v.trim()) localStorage.setItem(KEYS.yikeKeySecret, v.trim());
  else localStorage.removeItem(KEYS.yikeKeySecret);
}
export function getYikeEndpoint(): string { return localStorage.getItem(KEYS.yikeEndpoint) || ''; }
export function setYikeEndpoint(v: string): void {
  if (v.trim()) localStorage.setItem(KEYS.yikeEndpoint, v.trim());
  else localStorage.removeItem(KEYS.yikeEndpoint);
}
export function hasYikeCredentials(): boolean {
  return !!(getYikeKeyId() && getYikeKeySecret());
}

// ── Paid / publicly-published stories ─────────────────────────────────────────
export interface PaidStoryEntry {
  captureId: string;
  amount: number;
  tier: string;
  paidAt: number;
}

function readPaidStoryMap(): Record<string, PaidStoryEntry> {
  try {
    const raw = JSON.parse(localStorage.getItem(KEYS.paidStories) || '{}');
    return raw && typeof raw === 'object' ? raw : {};
  } catch {
    return {};
  }
}

function writePaidStoryMap(map: Record<string, PaidStoryEntry>): void {
  localStorage.setItem(KEYS.paidStories, JSON.stringify(map));
}

export function isPaidStory(captureId: string): boolean {
  if (!captureId) return false;
  return !!readPaidStoryMap()[captureId];
}

export function getPaidStoryInfo(captureId: string): PaidStoryEntry | null {
  if (!captureId) return null;
  return readPaidStoryMap()[captureId] || null;
}

export function markStoryPaid(entry: Omit<PaidStoryEntry, 'paidAt'>): PaidStoryEntry {
  const map = readPaidStoryMap();
  const full: PaidStoryEntry = { ...entry, paidAt: Date.now() };
  map[entry.captureId] = full;
  writePaidStoryMap(map);
  return full;
}

// ── Legacy fable user token (plaintext, returned once at /users/claim) ─────────
/** @deprecated Use getFableAccessToken() for new password-based auth. */
export function getUserToken(): string | null {
  return localStorage.getItem(KEYS.userToken);
}
/** @deprecated */
export function setUserToken(token: string): void {
  if (token.trim()) localStorage.setItem(KEYS.userToken, token.trim());
  else localStorage.removeItem(KEYS.userToken);
}
/** @deprecated */
export function clearUserToken(): void {
  localStorage.removeItem(KEYS.userToken);
}

// ── Fable JWT access/refresh token management ──────────────────────────────────

export interface FableSession {
  nickname: string;
  accessToken: string;
  refreshToken: string;
  expiresAt: number; // ms
}

export function getFableSession(): FableSession | null {
  const nickname = getNickname();
  const accessToken = localStorage.getItem(KEYS.accessToken);
  const refreshToken = localStorage.getItem(KEYS.refreshToken);
  const expiresAt = parseInt(localStorage.getItem(KEYS.accessTokenExpiresAt) || '0', 10);
  if (!nickname || !accessToken || !refreshToken) return null;
  return { nickname, accessToken, refreshToken, expiresAt };
}

export function setFableSession(session: Omit<FableSession, 'expiresAt'> & { expiresIn: number }): void {
  const expiresAt = Date.now() + session.expiresIn * 1000 - 60_000; // 1 min early buffer
  localStorage.setItem(KEYS.nickname, session.nickname);
  localStorage.setItem(KEYS.accessToken, session.accessToken);
  localStorage.setItem(KEYS.refreshToken, session.refreshToken);
  localStorage.setItem(KEYS.accessTokenExpiresAt, String(expiresAt));
}

export function getFableAccessToken(): string | null {
  return localStorage.getItem(KEYS.accessToken);
}

export function getFableRefreshToken(): string | null {
  return localStorage.getItem(KEYS.refreshToken);
}

export function isFableAccessTokenExpired(): boolean {
  const expiresAt = parseInt(localStorage.getItem(KEYS.accessTokenExpiresAt) || '0', 10);
  return !expiresAt || Date.now() > expiresAt;
}

export function clearFableSession(): void {
  localStorage.removeItem(KEYS.accessToken);
  localStorage.removeItem(KEYS.refreshToken);
  localStorage.removeItem(KEYS.accessTokenExpiresAt);
  // Keep nickname in storage so the login form can pre-fill it
}

// ── Cached star balance (refreshed after like / publish actions) ──────────────
export function getCachedStars(): number {
  const raw = parseInt(localStorage.getItem(KEYS.cachedStars) || '', 10);
  return Number.isNaN(raw) ? 0 : Math.max(0, raw);
}
export function setCachedStars(n: number): void {
  localStorage.setItem(KEYS.cachedStars, String(Math.max(0, n)));
}

// ── Scroll Positions ──────────────────────────────────────────────────────────
export function getScrollPosition(spaceId: string): number {
  try {
    const map: Record<string, number> = JSON.parse(
      localStorage.getItem(KEYS.scrollPositions) || '{}'
    );
    return map[spaceId] ?? 0;
  } catch {
    return 0;
  }
}

export function setScrollPosition(spaceId: string, scrollTop: number): void {
  try {
    const map: Record<string, number> = JSON.parse(
      localStorage.getItem(KEYS.scrollPositions) || '{}'
    );
    map[spaceId] = scrollTop;
    localStorage.setItem(KEYS.scrollPositions, JSON.stringify(map));
  } catch {
    /* ignore */
  }
}
